import { FC, useEffect, useMemo, useState } from "react";
import { IWorkTg, workTgService } from "../../../entities/workTg";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { useMyActions } from "../../../entities/my";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { MyError } from "../../../shared/lib/error/MyError";
import { IItem } from "../../../shared/model/types";
import { MyButton, MyButtonSecondary } from "../../../shared/ui/button";
import { ConfirmationAction } from "../../../shared/ui/confirmationAction";
import { DropDownListSelected } from "../../../shared/ui/dropDownSelected";
import { Modal } from "../../../shared/ui/modal";
import classes from './moveUserWorkTg.module.scss'

interface IProps {
    userId: number;
    currentTgId?: number | null;
    currentTgName?: string;
    onSuccess: () => Promise<void> | void;
}

export const MoveUserWorkTg: FC<IProps> = ({userId, currentTgId, currentTgName, onSuccess}) => {
    const [open, setOpen] = useState(false)
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [workTgs, setWorkTgs] = useState<IWorkTg[]>([])
    const [selectedTgId, setSelectedTgId] = useState<number>(0)
    const [error, setError] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [isMoving, setIsMoving] = useState(false)
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()

    const availableWorkTgs = useMemo(
        () => workTgs.filter(workTg => workTg.id !== currentTgId && workTg.name !== currentTgName),
        [currentTgId, currentTgName, workTgs]
    )
    const workTgItems = useMemo<IItem<number>[]>(
        () => availableWorkTgs.map(workTg => ({id: workTg.id, name: workTg.name})),
        [availableWorkTgs]
    )

    const selectedTgName = availableWorkTgs.find(workTg => workTg.id === selectedTgId)?.name || ''

    const handleError = (e: unknown, defaultMessage: string) => {
        console.log(e)
        if(e instanceof AuthError){
            if(e.status === 401){
                setIsAuth(false)
            }
            setError(e.message)
            setGlobalMessage({message: e.message, type: 'error'})
            return
        }
        if(e instanceof MyError){
            setError(e.message)
            setGlobalMessage({message: e.message, type: 'error'})
            return
        }
        setError(defaultMessage)
        setGlobalMessage({message: defaultMessage, type: 'error'})
    }

    const loadWorkTgs = async () => {
        try{
            setIsLoading(true)
            setError('')
            const items = await workTgService.getAll()
            setWorkTgs(items)
        }
        catch(e){
            handleError(e, 'Ошибка при получении списка рабочих TG')
        }
        finally{
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if(open){
            setConfirmOpen(false)
            setSelectedTgId(0)
            loadWorkTgs()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    const close = () => {
        if(isMoving){
            return
        }
        setOpen(false)
        setConfirmOpen(false)
        setSelectedTgId(0)
        setError('')
    }

    const prepareMove = () => {
        if(!selectedTgId){
            setError('Выберите новый рабочий TG')
            return
        }
        setError('')
        setConfirmOpen(true)
    }

    const onSelectedWorkTg = (item: IItem<number>) => {
        return (selected: boolean) => {
            setSelectedTgId(selected ? item.id : 0)
            setError('')
        }
    }

    const move = async () => {
        if(!selectedTgId){
            setError('Выберите новый рабочий TG')
            setConfirmOpen(false)
            return
        }

        try{
            setIsMoving(true)
            setError('')
            await workTgService.moveUser(userId, selectedTgId)
            await onSuccess()
            setGlobalMessage({message: 'Рабочий TG сотрудника изменен', type: 'ok'})
            setOpen(false)
            setConfirmOpen(false)
            setSelectedTgId(0)
        }
        catch(e){
            setConfirmOpen(false)
            handleError(e, 'Ошибка при переносе на другой рабочий TG')
        }
        finally{
            setIsMoving(false)
        }
    }

    if(!currentTgId && !currentTgName){
        return null
    }

    return (
        <>
            <MyButton onClick={() => setOpen(true)}>
                Перенести на другой рабочий TG
            </MyButton>
            <Modal open={open} setOpen={(value) => !value && close()}>
                {confirmOpen ? (
                    <ConfirmationAction
                        onClick={move}
                        setOpen={(value) => {
                            if(!value) setConfirmOpen(false)
                        }}
                        title={`Будут перенесены связанные студенты, регулярные расписания, уроки и пробные уроки на выбранный рабочий TG${selectedTgName ? ` ${selectedTgName}` : ''}`}
                        type="send"
                        isLoading={isMoving}
                    />
                ) : (
                    <section className={classes.modal}>
                        <section className={classes.title}>Перенести на другой рабочий TG</section>
                        <section className={classes.current}>
                            Текущий основной TG: {currentTgName || `#${currentTgId}`}
                        </section>
                        <section className={classes.field}>
                            <span className={classes.label}>Новый рабочий TG</span>
                            <DropDownListSelected<number>
                                isLoading={isLoading}
                                items={workTgItems}
                                selectedIdItems={selectedTgId ? [selectedTgId] : []}
                                onSelected={onSelectedWorkTg}
                                plainSelected
                                placeholder="Выберите TG"
                                search
                                searchPlaceholder="Поиск по рабочим TG"
                                emptyText="TG не найдены"
                            />
                        </section>
                        {!isLoading && availableWorkTgs.length === 0 && (
                            <section className={classes.muted}>Нет доступных TG для переноса</section>
                        )}
                        {error && <section className={classes.error}>*{error}</section>}
                        <section className={classes.actions}>
                            <MyButtonSecondary onClick={close}>Отмена</MyButtonSecondary>
                            <MyButton
                                onClick={prepareMove}
                                disabled={isLoading || availableWorkTgs.length === 0}
                            >
                                Продолжить
                            </MyButton>
                        </section>
                    </section>
                )}
            </Modal>
        </>
    )
}
